import type { DateTime } from "luxon";
import { NotificationType, type NurseFullRole, type OpenShiftRequest, RequestStatus, type ShiftRequest } from "./models";

/** The shift being staffed, described well enough to tell whether it makes an open shift redundant. */
export interface StaffedShiftTarget {
	departmentId: string;
	shiftStart: DateTime;
	shiftEnd: DateTime;
	location: string;
	/** Job of the shift being staffed. Only compared when the department schedules by job. */
	jobId?: string;
	/** Role of the staff member being added — stands in for the job where jobs aren't used. */
	role: NurseFullRole;
	/** `JobManagementEnabled` for the department. */
	jobManagementEnabled: boolean;
	isCharge: boolean;
}

export const isOpenShiftRequest = (request: ShiftRequest): request is OpenShiftRequest =>
	request.request_type === NotificationType.OpenShift && "needed_nurses" in request;

/**
 * A posted request, as the gap views count one: it carries `needed_nurses`, the openings still left. Wider than
 * `isOpenShiftRequest` on purpose: `RequestsContext` writes the key onto EVERY request it maps, replacements and swaps
 * included, so this admits them too — the desktop's posted-request matcher places them on holes that way, and the
 * gaps Posted tab lists them. Narrowing it is a product decision, not a refactor.
 */
export const isPostedRequest = (request: ShiftRequest): request is OpenShiftRequest => "needed_nurses" in request;

/**
 * A posted request that is still open (`RequestStatus.Active`): what the desktop's posted-request matcher places on the
 * gap grid, and what the gaps day's offered pick-up bonus sums.
 */
export const isActivePostedRequest = (request: ShiftRequest): request is OpenShiftRequest =>
	isPostedRequest(request) && request.status === RequestStatus.Active;

// Never matched: only `sub_role` separates otherwise-identical postings, the add surfaces have none,
// and retiring one cancels the shift at the agency.
const isAgencyPosted = (request: OpenShiftRequest): boolean => Boolean(request.agency_source_name || request.agency_shift_ids?.length || request.sub_role);

const namesTargetLocation = (request: OpenShiftRequest, target: StaffedShiftTarget): boolean =>
	Boolean(target.location) && request.location === target.location;

/**
 * The active open shift that staffing `target` makes redundant. A blank location on either side means
 * "any location" — an unconstrained need, not a mismatch — though a request naming the location
 * actually staffed wins over a department-wide one, and otherwise the oldest match does.
 *
 * Job is only compared where the department schedules by job: `requests.job_id` is NOT NULL and falls
 * back to an 'unknown' sentinel, so comparing it elsewhere would reject every candidate. There the
 * posted roles stand in for it.
 */
export const findRedundantOpenShift = (requests: ShiftRequest[], target: StaffedShiftTarget): OpenShiftRequest | undefined =>
	requests
		.filter(isOpenShiftRequest)
		.filter((request) => request.status === RequestStatus.Active)
		.filter((request) => request.department_id === target.departmentId)
		.filter((request) => !isAgencyPosted(request))
		.filter((request) => request.message.shift_start.hasSame(target.shiftStart, "minute") && request.message.shift_end.hasSame(target.shiftEnd, "minute"))
		// Falsy check, not `=== false`: charge_only is nullable in practice despite the model.
		.filter((request) => !request.charge_only || target.isCharge)
		.filter((request) => !request.location || !target.location || request.location === target.location)
		.filter((request) => (target.jobManagementEnabled ? request.job_id === target.jobId : request.message.roles.includes(target.role)))
		.sort(
			(first, second) =>
				Number(namesTargetLocation(second, target)) - Number(namesTargetLocation(first, target)) || first.created_at.toMillis() - second.created_at.toMillis(),
		)[0];
